import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { QuestionState } from './types';
import { RootState } from '../../../../app/store';

import {
  getFreeTextAnswersApiClient,
  getMultipleChoiceAnswersApiClient,
  getQuestionsApiClient
} from '../../../../common/apiClients';
import { getMessageFromError } from '../../../../common/utils/apiUtils';
import {
  FreeTextAnswer,
  FreeTextAnswerCreatePayloadRequest,
  FreeTextAnswersCreateRequest,
  FreeTextAnswersDestroyRequest,
  FreeTextAnswersListRequest,
  FreeTextAnswersUpdateRequest,
  FreeTextAnswerUpdatePayloadRequest,
  MultipleChoiceAnswer,
  MultipleChoiceAnswerCreatePayloadRequest,
  MultipleChoiceAnswersCreateRequest,
  MultipleChoiceAnswersDestroyRequest,
  MultipleChoiceAnswersListRequest,
  MultipleChoiceAnswersUpdateRequest,
  MultipleChoiceAnswerUpdatePayloadRequest,
  Question,
  QuestionsCreateRequest,
  QuestionsDestroyRequest,
  QuestionsImageRetrieveRequest,
  QuestionsListRequest,
  QuestionsUpdateRequest
} from '../../../../generated/api';

const sliceName = 'questions';

export const fetchQuestionsAsync = createAsyncThunk<Question[], number>(
  `${sliceName}/fetchQuestions`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getQuestionsApiClient();
      const request: QuestionsListRequest = { categoryId: String(arg) };
      return await apiClient.questionsList(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const createQuestionAsync = createAsyncThunk<Question, QuestionsCreateRequest>(
  `${sliceName}/createQuestions`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getQuestionsApiClient();
      return await apiClient.questionsCreate(arg);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const updateQuestionAsync = createAsyncThunk<Question, QuestionsUpdateRequest>(
  `${sliceName}/updateQuestions`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getQuestionsApiClient();
      return await apiClient.questionsUpdate(arg);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const destroyQuestionAsync = createAsyncThunk<void, number>(
  `${sliceName}/destroyQuestion`,
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      const apiClient = await getQuestionsApiClient();
      const request: QuestionsDestroyRequest = { id: arg };
      await apiClient.questionsDestroy(request);
      dispatch(removeQuestion(arg));
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const fetchImageAsync = createAsyncThunk<Blob, number>(
  `${sliceName}/fetchImage`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getQuestionsApiClient();
      const request: QuestionsImageRetrieveRequest = { id: arg };
      return await apiClient.questionsImageRetrieve(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const fetchMCAnswersAsync = createAsyncThunk<MultipleChoiceAnswer[], MultipleChoiceAnswersListRequest>(
  `${sliceName}/fetchMCAnswers`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getMultipleChoiceAnswersApiClient();
      return await apiClient.multipleChoiceAnswersList(arg);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const createMCAnswerAsync = createAsyncThunk<MultipleChoiceAnswer, MultipleChoiceAnswerCreatePayloadRequest>(
  `${sliceName}/createMCAnswer`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getMultipleChoiceAnswersApiClient();
      const request: MultipleChoiceAnswersCreateRequest = { multipleChoiceAnswerCreatePayloadRequest: arg };
      return await apiClient.multipleChoiceAnswersCreate(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const updateMCAnswerAsync = createAsyncThunk<
  MultipleChoiceAnswer,
  { id: number; data: MultipleChoiceAnswerUpdatePayloadRequest }
>(`${sliceName}/updateMCAnswers`, async (arg, { rejectWithValue }) => {
  try {
    const apiClient = await getMultipleChoiceAnswersApiClient();
    const request: MultipleChoiceAnswersUpdateRequest = {
      id: arg.id,
      multipleChoiceAnswerUpdatePayloadRequest: arg.data
    };
    return await apiClient.multipleChoiceAnswersUpdate(request);
  } catch (e) {
    return rejectWithValue(await getMessageFromError(e));
  }
});

export const destroyMCAnswerAsync = createAsyncThunk<void, number>(
  `${sliceName}/destroyMCAnswer`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getMultipleChoiceAnswersApiClient();
      const request: MultipleChoiceAnswersDestroyRequest = { id: arg };
      return await apiClient.multipleChoiceAnswersDestroy(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const fetchFTAnswerAsync = createAsyncThunk<FreeTextAnswer[], FreeTextAnswersListRequest>(
  `${sliceName}/fetchFTAnswer`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getFreeTextAnswersApiClient();
      return await apiClient.freeTextAnswersList(arg);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const createFTAnswerAsync = createAsyncThunk<FreeTextAnswer, FreeTextAnswerCreatePayloadRequest>(
  `${sliceName}/createFTAnswer`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getFreeTextAnswersApiClient();
      const request: FreeTextAnswersCreateRequest = { freeTextAnswerCreatePayloadRequest: arg };
      return await apiClient.freeTextAnswersCreate(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const updateFTAnswerAsync = createAsyncThunk<
  FreeTextAnswer,
  { id: number; data: FreeTextAnswerUpdatePayloadRequest }
>(`${sliceName}/updateFTAnswers`, async (arg, { rejectWithValue }) => {
  try {
    const apiClient = await getFreeTextAnswersApiClient();
    const request: FreeTextAnswersUpdateRequest = { id: arg.id, freeTextAnswerUpdatePayloadRequest: arg.data };
    return await apiClient.freeTextAnswersUpdate(request);
  } catch (e) {
    return rejectWithValue(await getMessageFromError(e));
  }
});

export const destroyFTAnswerAsync = createAsyncThunk<void, number>(
  `${sliceName}/destroyFTAnswer`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getFreeTextAnswersApiClient();
      const request: FreeTextAnswersDestroyRequest = { id: arg };
      return await apiClient.freeTextAnswersDestroy(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

const initialState: QuestionState = {
  questions: [],
  loading: false
};

const questionSlice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    clearQuestions: (state) => {
      return { ...state, questions: [] };
    },
    removeQuestion: (state, action: PayloadAction<number>): QuestionState => {
      return { ...state, questions: state.questions.filter((question) => question.id !== action.payload) };
    }
  },
  extraReducers: {
    [fetchQuestionsAsync.fulfilled.type]: (state, action: PayloadAction<Question[]>): QuestionState => {
      return { ...state, questions: action.payload, loading: false };
    },
    [createQuestionAsync.fulfilled.type]: (state, action: PayloadAction<Question>): QuestionState => {
      return { ...state, questions: [...state.questions, action.payload] };
    },
    [updateQuestionAsync.fulfilled.type]: (state, action: PayloadAction<Question>): QuestionState => {
      return {
        ...state,
        questions: state.questions.map((question) => (question.id === action.payload.id ? action.payload : question))
      };
    }
  }
});

export const selectQuestions = (state: RootState): QuestionState => state.questions;
export const { removeQuestion, clearQuestions } = questionSlice.actions;
export default questionSlice.reducer;
